import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { useLegalCheckbox } from "@sundae/react-hooks";
import { utils, } from "@sundae/wallets";
import cx from "classnames";
import { useCallback, useState } from "react";
import Button from "../../atoms/Button";
import { ExternalLink } from "../../atoms/Link";
import Loader from "../../atoms/Loader";
import LegalCheckbox from "../../molecules/LegalCheckbox";
import Notice from "../../molecules/Notice";
import P2PWalletButton from "../../molecules/P2PWalletButton/P2PWalletButton";
import WalletButton from "../../molecules/WalletButton";
/**
 * WalletConnectFlow is a user interface component for managing the wallet connection flow.
 * It provides an interface for users to select, connect and interact with wallets.
 * Can be used within a WalletConnectDialog or for inline rendering inside the UI.
 *
 * @component
 * @param {IWalletConnectFlow} props - The props for the WalletConnectFlow component.
 * @param {II18N} props.i18n - An object containing localized text for the component.
 * @param {string} [props.i18n.description] - Description text displayed within the component.
 * @param {string} [props.i18n.error] - Error message text displayed within the component.
 * @param {string} [props.i18n.learnMore] - Learn More link text displayed within the component.
 * @param {string} [props.i18n.suggestWallet] - Suggest Wallet link text displayed within the component.
 * @param {string} [props.i18n.noWallets] - No Wallets text displayed within the component.
 * @param {string} [props.i18n.selectWalletNotice] - Select Wallet Notice text displayed within the component.
 * @param {string} [props.i18n.title] - Title text displayed within the component.
 * @param {string} [props.i18n.walletConnect] - Wallet Connect button text displayed within the component.
 * @param {boolean} props.isLoading - Whether the component is in a loading state.
 * @param {string} [props.learnMoreRoute] - The route for the "Learn More" link.
 * @param {ReactNode} props.legalDisclaimer - The legal disclaimer content to be displayed.
 * @param {(key: KnownCip30WalletId, wallet: TInitialApi) => void} props.onConnectWallet - Callback function to be called when a wallet is connected.
 * @param {string[]} [props.allowList] - An optional array of wallet IDs to be displayed. All other wallets will be hidden.
 * @param {string[]} [props.ignoreList] - An optional array of wallet IDs to be hidden.
 * @param {ReactNode} props.noWalletsComponent - The component to be displayed when no wallets are available.
 * @param {boolean} [props.onlyKnown] - Whether to only show known wallets.
 * @param {string} [props.suggestWalletRoute] - The route for the "Suggest Wallet" link.
 * @param {boolean} [props.iframe] - Whether the component should be rendered within an iframe.
 * @returns {JSX.Element} A rendered WalletConnectFlow component.
 */
const WalletConnectFlow = ({ i18n, isLoading, learnMoreRoute, legalDisclaimer, onConnectWallet, allowList, ignoreList, noWalletsComponent, onlyKnown, suggestWalletRoute, iframe = false, cip45, }) => {
    const [selectedWallet, setSelectedWallet] = useState();
    const [showSelectWalletNotice, setShowSelectWalletNotice] = useState(false);
    const [P2PWalletSelected, setP2PWalletSelected] = useState(false);
    const { isLegalChecked, handleCheckbox: handleLegalCheckbox, handleErrorFlash, showErrorFlash, } = useLegalCheckbox();
    const wallets = utils.getAvailableWallets({
        allowList,
        ignoreList,
        onlyKnown,
        iframe,
    });
    const sortedWallets = wallets?.sort((a, b) => {
        return a[0].localeCompare(b[0]);
    });
    const userHasWallet = Boolean(sortedWallets?.length);
    const onClickP2PButton = useCallback(() => {
        setP2PWalletSelected((prev) => !prev);
        setSelectedWallet(undefined);
        setShowSelectWalletNotice(false);
    }, []);
    const renderContent = () => {
        if (!userHasWallet) {
            return noWalletsComponent;
        }
        return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "grid grid-cols-2 gap-4 lg:grid-cols-3 xl:gap-5", children: [sortedWallets.map(([key, wallet], index) => {
                            const isSelected = selectedWallet?.key === key;
                            if (wallet.icon) {
                                return (_jsx(WalletButton, { isSelected: isSelected, onClick: () => {
                                        // @TODO: implement once information is available
                                        /** if (linkToWallet) {
                                            window.open(linkToWallet, "_blank");
                                          } */
                                        if (isSelected) {
                                            setSelectedWallet(undefined);
                                            return;
                                        }
                                        setShowSelectWalletNotice(false);
                                        setSelectedWallet({ key, wallet });
                                        setP2PWalletSelected(false);
                                    }, wallet: wallet, walletId: key }, `${key}-${index}`));
                            }
                            return null;
                        }), cip45 && (_jsx(P2PWalletButton, { ...cip45, isSelected: P2PWalletSelected, onClick: onClickP2PButton }))] }), _jsx(LegalCheckbox, { isLegalChecked: isLegalChecked, onLegalClick: handleLegalCheckbox, showErrorFlash: showErrorFlash, children: legalDisclaimer })] }));
    };
    return (_jsx(ScrollArea, { children: _jsxs("div", { className: "relative flex w-full flex-col items-center justify-start gap-6 md:min-w-[25rem] md:max-w-[31rem]", children: [isLoading && userHasWallet && (_jsx("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2", children: _jsx(Loader, {}) })), _jsxs("div", { "aria-labelledby": "available-wallets", className: cx("grid min-h-[15rem] w-full gap-6 transition duration-300", {
                        "pointer-events-none blur-sm": isLoading,
                        "!flex flex-col items-center justify-center": !userHasWallet,
                    }), children: [renderContent(), i18n.error && !isLoading && (_jsx(Notice, { className: "mt-2 w-full", size: "xs", type: "error", children: i18n.error })), !isLoading && showSelectWalletNotice && (_jsx(Notice, { className: "mt-2 w-full", size: "xs", type: "warning", children: i18n.selectWalletNotice ??
                                "Please select a wallet that you want to connect." })), _jsxs("div", { className: "grid w-full gap-4 lg:flex lg:items-center lg:justify-between", children: [((i18n.learnMore && learnMoreRoute) ||
                                    (i18n.suggestWallet && suggestWalletRoute)) && (_jsxs("div", { className: "order-2 grid items-center gap-2 md:flex md:justify-between lg:order-1 lg:grid", children: [learnMoreRoute && (_jsx(ExternalLink, { arrow: "top-right", size: "xs", variant: "muted", href: learnMoreRoute, children: i18n.learnMore })), suggestWalletRoute && (_jsx(ExternalLink, { arrow: "top-right", size: "xs", variant: "silent", href: suggestWalletRoute, children: i18n.suggestWallet }))] })), _jsx(Button, { className: "order-1 lg:order-2 lg:w-fit", onClick: () => {
                                        if (!selectedWallet && !P2PWalletSelected) {
                                            setShowSelectWalletNotice(true);
                                            return;
                                        }
                                        if (!isLegalChecked) {
                                            handleErrorFlash();
                                            return;
                                        }
                                        if (selectedWallet) {
                                            onConnectWallet(selectedWallet.key, selectedWallet.wallet);
                                        }
                                        else {
                                            cip45?.setCip45DialogOpen(true);
                                            cip45?.onClick?.();
                                        }
                                    }, variant: "gradient", children: i18n.walletConnect ?? "Connect Wallet" })] })] })] }) }));
};
export default WalletConnectFlow;
//# sourceMappingURL=WalletConnectFlow.js.map